import React from 'react';
import { ROLE_HIERARCHY as ROLE_LEVEL } from '../../lib/rbacRoles';

// Shared building blocks for the Workforce pages. All copy is Italian, all
// styling goes through wfm-* classes / --wfm-* CSS vars.

export const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

export const REGIONI_IT = [
  ['IT-65', 'Abruzzo'], ['IT-77', 'Basilicata'], ['IT-78', 'Calabria'],
  ['IT-72', 'Campania'], ['IT-45', 'Emilia-Romagna'], ['IT-36', 'Friuli-Venezia Giulia'],
  ['IT-62', 'Lazio'], ['IT-42', 'Liguria'], ['IT-25', 'Lombardia'],
  ['IT-57', 'Marche'], ['IT-67', 'Molise'], ['IT-21', 'Piemonte'],
  ['IT-75', 'Puglia'], ['IT-88', 'Sardegna'], ['IT-82', 'Sicilia'],
  ['IT-52', 'Toscana'], ['IT-32', 'Trentino-Alto Adige'], ['IT-55', 'Umbria'],
  ['IT-23', "Valle d'Aosta"], ['IT-34', 'Veneto'],
];

export function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function fmtNum(n, dec = 2) {
  if (n == null || Number.isNaN(Number(n))) return '0';
  return Number(n).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: dec });
}

export function fmtMoney(n, valuta = 'EUR') {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try {
    return Number(n).toLocaleString('it-IT', { style: 'currency', currency: valuta });
  } catch (_) {
    return `${fmtNum(n)} ${valuta}`;
  }
}

export function userLabel(u) {
  if (!u) return '—';
  return `${u.nome || ''} ${u.cognome || ''}`.trim() || u.email || u.id;
}

// PageHeader / Loading / EmptyState / Modal now live in components/ui — they
// are app-wide primitives, not Workforce ones. Re-exported here so the 15
// existing `wfShared` importers keep working; new code should import them from
// '../../components/ui' directly.
export { default as PageHeader } from '../../components/ui/PageHeader';
export { default as Loading } from '../../components/ui/Loading';
export { default as EmptyState } from '../../components/ui/EmptyState';
export { default as Modal } from '../../components/ui/Modal';

// Stato → badge palette for ferie requests.
export function StatoBadge({ stato }) {
  const map = {
    richiesta: ['var(--wfm-info-bg)', 'var(--wfm-info-text)', 'var(--wfm-info-border)', 'Richiesta'],
    approvata: ['var(--wfm-success-bg)', 'var(--wfm-success-text)', 'var(--wfm-success-border)', 'Approvata'],
    rifiutata: ['var(--wfm-error-bg)', 'var(--wfm-error-text)', 'var(--wfm-error-border)', 'Rifiutata'],
    annullata: ['var(--wfm-neutral-bg)', 'var(--wfm-neutral-text)', 'var(--wfm-neutral-border)', 'Annullata'],
  };
  const [bg, fg, border, label] = map[stato] || ['var(--wfm-neutral-bg)', 'var(--wfm-neutral-text)', 'var(--wfm-neutral-border)', stato];
  return (
    <span className="wfm-badge" style={{ backgroundColor: bg, color: fg, border: `1px solid ${border}` }}>
      {label}
    </span>
  );
}

export const TIPO_ASSENZA_LABEL = { ferie: 'Ferie', rol: 'ROL', ex_festivita: 'Ex-festività', banca_ore: 'Banca ore' };

// ─── Request approval hierarchy ─────────────────────────────────────────
// OPERATOR < ADMIN < MANAGER < SUPER_ADMIN. An actor may approve / reject a
// request — or cancel one already approved — only if strictly above the
// requester. SUPER_ADMIN manages everything, including its own level.
export { ROLE_LEVEL };
export function isStaffRole(ruolo) {
  return (ROLE_LEVEL[ruolo] || 0) >= 2;
}
export function canApprova(actorRuolo, requesterRuolo) {
  const a = ROLE_LEVEL[actorRuolo] || 0;
  const r = ROLE_LEVEL[requesterRuolo] || 0;
  return a > 0 && (a > r || a === 4);
}

// Break rule: a shift longer than 6 gross hours requires at least a 60-minute
// break; shorter shifts have no minimum (the break stays 0 / whatever the user
// types). `grossMinutes` = uscita − entrata (no break subtracted).
export const PAUSA_THRESHOLD_MIN = 6 * 60;
export function minimumPausa(grossMinutes) {
  return grossMinutes > PAUSA_THRESHOLD_MIN ? 60 : 0;
}

// Today as YYYY-MM-DD (local).
export function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Round a Date to the nearest 5 minutes → local { data:'YYYY-MM-DD', ora:'HH:MM' }.
// Rounding the whole timestamp handles hour/day rollover. Used by the clock-in
// flows (Home PunchClock + staff Entrata on Presenze) so the times sent to the
// server are the user's local 5-minute-rounded values (no server-TZ skew).
export function roundTo5(d = new Date()) {
  const step = 5 * 60 * 1000;
  const r = new Date(Math.round(d.getTime() / step) * step);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    data: `${r.getFullYear()}-${pad(r.getMonth() + 1)}-${pad(r.getDate())}`,
    ora: `${pad(r.getHours())}:${pad(r.getMinutes())}`,
  };
}

// First/last day of the current month as YYYY-MM-DD.
export function monthRange() {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const f = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { dal: f(first), al: f(last) };
}
